import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import SharedRitualFilm from "./SharedRitualFilm";

const reduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* Modes: "animation" (abstract composition, now) | "photo" (approved photography) | "video" (uploaded, muted inline) */
export default function HeroMedia({ media, label, caption, className = "" }) {
  const mode = media?.mode || "animation";
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [userPaused, setUserPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const [visible, setVisible] = useState(true);
  const isStatic = reduced();

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const paused = userPaused || !inView || !visible;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused || isStatic) v.pause();
    else v.play().catch(() => {});
  }, [paused, isStatic]);

  const showControl = mode === "video" && !isStatic;

  if (mode === "animation") {
    return (
      <div ref={ref} className={`relative ${className}`} data-testid="hero-media" data-mode="animation">
        <SharedRitualFilm />
      </div>
    );
  }

  return (
    <figure ref={ref} className={`relative ${className}`} data-testid="hero-media" data-mode={mode}>
      <div className="frame">
        <div className="relative overflow-hidden aspect-[4/5] bg-linen">
          {mode === "photo" && (
            <img src={media.image_url} alt={media.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
          )}

          {mode === "video" && (
            isStatic ? (
              <img src={media.poster_url} alt={media.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <video
                ref={videoRef}
                src={media.video_url}
                poster={media.poster_url || undefined}
                muted
                loop
                playsInline
                autoPlay
                preload="metadata"
                aria-label={media.alt || ""}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )
          )}

          {showControl && (
            <button
              type="button"
              onClick={() => setUserPaused((p) => !p)}
              aria-pressed={userPaused}
              aria-label={userPaused ? "Play animation" : "Pause animation"}
              data-testid="hero-media-toggle"
              className="absolute top-4 right-4 z-10 w-10 h-10 inline-flex items-center justify-center rounded-full glass text-forest border border-forest/15 hover:border-forest/50 transition-colors"
            >
              {userPaused ? <Play size={15} strokeWidth={1.5} /> : <Pause size={15} strokeWidth={1.5} />}
            </button>
          )}

          <figcaption
            data-testid="hero-media-caption"
            className="hidden sm:block absolute left-6 bottom-6 right-6 lg:right-auto lg:max-w-[300px] z-10 glass text-forest px-6 py-5 border border-forest/10"
          >
            <p className="text-[10px] uppercase tracking-[0.24em] text-moss font-medium">{label}</p>
            <p className="mt-2 font-serif text-lg leading-snug">{caption}</p>
          </figcaption>
        </div>
      </div>
      <figcaption className="sm:hidden mt-8 border-l border-champagne pl-5 text-ivory">
        <p className="text-[10px] uppercase tracking-[0.24em] text-champagne font-medium">{label}</p>
        <p className="mt-2 font-serif text-lg leading-snug text-ivory/90">{caption}</p>
      </figcaption>
    </figure>
  );
}
